import { useAuthState } from "@/contexts/auth/auth.provider";
import { useMutation } from "@apollo/client";
import { REPORT_IMAGES_ISSUE } from "@/graphql/mutations/report";
import React, { useRef } from "react";
import { useFavListingsState } from "@/contexts/fav-listings/favListings.provider";
import { usePath } from "@/contexts/path/path";
import {
  addImgReportedID,
  CloudfrontImageLoader,
  financialFormat,
  getSpecs,
  getStoredImgsReportedIDs,
} from "@/lib/helpers";
import css from "./.module.scss";
import Link from "next/link";
import Image from "next/image";
import Icon from "@/components/icon";
import AppLink from "@/components/new-resolver/app.ink";
import { getPrice } from "@/components/Listing/ListingCard/index";

export const getImageSrc = (images) =>
  Array.isArray(images) && images[0] ? images[0] : "";
export function OwnerListingCard({
  onClick,
  listing,
  preloadImage,
  index,
  imageClass,
  trackClickEvent,
  horizontal,
}) {
  const {
    id,
    title,
    published,
    district,
    content,
    city,
    special,
    sov_campaign_id,
    status,
    licensing_request,
    pending_payment,
    whatsapp_requested_count = 0,
    whatsapp_responded_count = 0,
  } = listing || {};
  const { updateContext, filters, basePath, categoriesDictionary } = usePath();

  const token = useAuthState("token");

  const requests = whatsapp_requested_count - whatsapp_responded_count;
  const price = getPrice(listing, filters);
  const [reportImagesIssue] = useMutation(REPORT_IMAGES_ISSUE, {
    skip: !token,
    context: { token },
  });
  const image = getImageSrc(listing.imgs);
  const imageWrapper = useRef();

  const isFavorite = useFavListingsState(listing.id);
  const specs = getSpecs(listing);
  const isLicenseExpired = status === 10;
  return (
    <div className={css.link}>
      <AppLink
        onClick={(e) => {
          if ((basePath || "").includes("map")) {
            e.stopPropagation();
            e.preventDefault();
          }
          if (onClick) return onClick(e, index);
          if (!listing.path) return;
          trackClickEvent && trackClickEvent();
          return updateContext({
            currentFrozenIndex: index,
            listing: {
              ...listing,
              loadedImage: imageWrapper.current?.firstChild?.firstChild
                ?.complete
                ? imageWrapper.current.firstChild?.firstChild.currentSrc
                : undefined,
            },
          });
        }}
        path={listing.path || `/ad/${listing.id}`}
        scroll
      >
        <div
          className={css.listingCard + " " + (horizontal ? css.horizontal : "")}
        >
          <div className={css.absolute}>
            {(special || sov_campaign_id) && (
              <div
                className={
                  css.sov /*sov_campaign_id ? css.sov : css.specialText*/
                }
              >
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                    />
                  </svg>
                </span>
                <p> مميز</p>
              </div>
            )}

            {isFavorite && (
              <button type="button" className={css.clickHandler}>
                <Icon
                  name="BorderHeart-colored"
                  alt="مفضلة"
                  width={25}
                  height={25}
                />
              </button>
            )}
          </div>

          <div className={css.content}>
            {horizontal ? null : (
              <div className={css.titleRow}>
                <h4>
                  {listing.title ||
                    listing.unit_code ||
                    categoriesDictionary?.[listing.category]?.name}
                </h4>
                {!!requests && (
                  <span className={css.whatsapp_requests}>
                    <span>{requests}</span>
                    <span>طلبات تواصل</span>
                  </span>
                )}
                {isLicenseExpired && (
                  <span className={css.licenseExpired}>ترخيص منتهي</span>
                )}
              </div>
            )}

            <p className={css.price}>{price}</p>
            <p className={css.notPublished}>
              {pending_payment
                ? "بانتظار الدفع"
                : licensing_request
                ? "جاري الترخيص"
                : published
                ? ""
                : "طلب تسويق"}
            </p>
            {!!specs?.length && (
              <div className={css.specs}>
                {specs.map(({ icon, value, alt }, index) => (
                  <div className={css.spec} key={`${icon}-${value}-${index}`}>
                    {icon && (
                      <Icon
                        name={icon}
                        alt={alt}
                        width={20}
                        height={20}
                        priority
                      />
                    )}
                    {value}
                  </div>
                ))}
              </div>
            )}

            {horizontal ? (
              <div className={css.description}>
                <p>
                  <small>{listing.title}</small>
                </p>
              </div>
            ) : (
              <>
                {content && (
                  <div className={css.description}>
                    <p>{content}</p>
                  </div>
                )}
              </>
            )}
            {horizontal ? null : (
              <div className={css.footer}>
                <p>
                  <span>{city}</span>
                  <span>{district ? `-${district}` : ""}</span>
                </p>
              </div>
            )}
          </div>
          <div
            ref={imageWrapper}
            className={css.imageWrapper + " " + imageClass}
          >
            <Image
              loader={CloudfrontImageLoader}
              src={image || "villa-default.png"}
              onError={() => {
                const reportedImgs = getStoredImgsReportedIDs();
                if (!(reportedImgs && reportedImgs[image]) && token) {
                  addImgReportedID(image);
                  reportImagesIssue({
                    variables: {
                      listing_id: id,
                      images: image,
                    },
                  });
                }
              }}
              style={{
                objectFit: "cover",
              }}
              fill
              alt={title || ""}
              sizes="(max-width: 600px) 40vw, 100vw"
              priority={preloadImage}
            />
          </div>
        </div>
      </AppLink>
    </div>
  );
}
export default OwnerListingCard;
